import { useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { IOSSwitch } from '@/components/IOSSwitch';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { Availability, AvailabilityInterval } from '@/types/api/companies';
import { getFirstValueTrue } from '@/utils/arrayUtils';
import { formatHour, WEEKDAYS } from '@/utils/components/institutionalPage';
import { DEFAULT_TIMEZONE } from '@/utils/constants';
import { handleErrorAsync } from '@/utils/handlers';
import { ArrowBack } from '@mui/icons-material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import WorkOffIcon from '@mui/icons-material/WorkOff';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Modal,
  Slide,
  Stack,
  Typography,
} from '@mui/material';
import {
  getDateFromAvailabilityInterval,
  useAvailabilities,
} from './availabilitiesReducer';
import { TimePicker } from './TimePicker';

interface IAvailabilitiesProps {
  isOpen: boolean;
  onClose: () => void;
  availabilities: Availability[];
  setAvailabilities: (availabilities: Availability[]) => void;
}

export const WEEKDAY_TEST_ID = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
} as const;

export function orderByWeekdays(availabilities: Availability[]) {
  return availabilities
    .sort((a, b) => (a.weekday > b.weekday ? 1 : -1))
    .map(interval => ({
      ...interval,
      intervals: interval.intervals.sort((a, b) =>
        new Date(a.created_at) < new Date(b.created_at) ? -1 : 1
      ),
    }));
}

export const Availabilities = ({
  isOpen,
  onClose,
  availabilities,
  setAvailabilities,
}: IAvailabilitiesProps) => {
  const { setIsLoading } = useLoading();
  const {
    state,
    editWeekday,
    addAvailabilityInterval,
    removeAvailabilityInterval,
    changeIsAlwaysOpen,
    editAvailabilityInterval,
  } = useAvailabilities({
    editingWeekday: undefined,
    availabilities: orderByWeekdays(availabilities),
  });
  const [backup, setBackup] = useState(state.availabilities);

  const availabilityWeekday = state.availabilities.find(
    ({ weekday }) => weekday === state.editingWeekday
  );

  function formatDateToAvailabilityInterval(date?: Date | number) {
    return (
      Intl.DateTimeFormat('pt-br', {
        timeStyle: 'short',
      }).format(date) + ':00'
    );
  }

  async function changeIsAlwaysOpenApi(isAlwaysOpen: boolean) {
    const [_, error] = await handleErrorAsync(async () => {
      setIsLoading(true);
      await api.put(`api/company_availabilities/${availabilityWeekday?.id}/`, {
        is_always_open: isAlwaysOpen,
      });
    }).finally(() => setIsLoading(false));

    if (error) return Toast('error', error.message);

    changeIsAlwaysOpen(isAlwaysOpen);
    setAvailabilities(
      state.availabilities.map(availability =>
        availability.weekday === availabilityWeekday?.weekday
          ? { ...availability, is_always_open: isAlwaysOpen }
          : availability
      )
    );
  }

  async function addAvailabilityIntervalApi() {
    const [newAvailabilityInterval, error] = await handleErrorAsync(
      async () => {
        setIsLoading(true);
        const { data } = await api.post<AvailabilityInterval>(
          'api/company_availability_intervals/',
          {
            availability: availabilityWeekday?.id,
            start: calculateIntervalToAdd('09:00:00', 1),
            end: calculateIntervalToAdd('18:00:00', 2),
            timezone: DEFAULT_TIMEZONE,
          }
        );
        return data;
      }
    ).finally(() => setIsLoading(false));

    if (error) return Toast('error', error.message);

    addAvailabilityInterval(newAvailabilityInterval);
    setAvailabilities(
      state.availabilities.map(availability => ({
        ...availability,
        intervals: availability.intervals.concat(newAvailabilityInterval),
      }))
    );
  }

  function calculateIntervalToAdd(fallback: string, incrementMinutes: number) {
    if (availabilityWeekday?.intervals.length === 0) {
      return fallback;
    }
    const endDate = getDateFromAvailabilityInterval(
      availabilityWeekday?.intervals[0].end || ''
    );
    return formatDateToAvailabilityInterval(
      endDate.setMinutes(endDate.getMinutes() + incrementMinutes)
    );
  }

  async function removeAvailabilityIntervalApi(availabilityIntervalId: number) {
    const [_, error] = await handleErrorAsync(async () => {
      setIsLoading(true);
      await api.delete<AvailabilityInterval>(
        `api/company_availability_intervals/${availabilityIntervalId}/`
      );
    }).finally(() => setIsLoading(false));

    if (error) return Toast('error', error.message);

    removeAvailabilityInterval({
      availabilityIntervalId: availabilityIntervalId,
    });
    setAvailabilities(
      state.availabilities.map(availability => ({
        ...availability,
        intervals: availability.intervals.filter(
          interval => interval.id !== availabilityIntervalId
        ),
      }))
    );
  }

  async function changeAvailabilityIntervalApi(
    availabilityIntervalUpdated:
      | {
          id: number;
          start: string;
        }
      | {
          id: number;
          end: string;
        }
  ) {
    const [_, error] = await handleErrorAsync(async () => {
      setIsLoading(true);

      await api.patch<AvailabilityInterval>(
        `api/company_availability_intervals/${availabilityIntervalUpdated.id}/`,
        {
          availability: availabilityWeekday?.id,
          ...availabilityIntervalUpdated,
        }
      );
    }).finally(() => setIsLoading(false));

    if (error && availabilityWeekday) {
      const availabilityBackup = backup[
        availabilityWeekday.weekday
      ].intervals.filter(({ id }) => availabilityIntervalUpdated.id === id)[0];

      editAvailabilityInterval(availabilityBackup);

      Toast('error', error.message);
      return;
    }

    setBackup(state.availabilities);
    setAvailabilities(
      state.availabilities.map(availability => ({
        ...availability,
        intervals: availability.intervals.map(interval =>
          interval.id === availabilityIntervalUpdated.id
            ? {
                ...interval,
                ...availabilityIntervalUpdated,
              }
            : interval
        ),
      }))
    );
  }

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={theme => ({
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          [theme.breakpoints.up('md')]: {
            borderRadius: '10px',
            height: '528px',
            left: 'calc(50% + 56px/2)',
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '456px',
          },
        })}
      >
        <Box
          component="header"
          sx={{
            alignItems: 'center',
            color: 'text.primary',
            backgroundColor: 'primary.contrastText',
            display: 'grid',
            gridTemplateColumns: '40px 1fr',
            marginBottom: 1,
            paddingY: 2,
            paddingLeft: 1,
            position: 'relative',
          }}
        >
          <IconButton
            color="inherit"
            onClick={() =>
              availabilityWeekday ? editWeekday(undefined) : onClose()
            }
          >
            <ArrowBack />
          </IconButton>

          <Typography
            component="h3"
            data-testid="title"
            id={state.editingWeekday ? 'availability-details' : ''}
            sx={{
              color: 'inherit',
              justifySelf: 'center',
              marginRight: '48px',
            }}
            variant="h6"
          >
            {availabilityWeekday
              ? WEEKDAYS[availabilityWeekday.weekday]
              : 'Horário de atendimento'}
          </Typography>
        </Box>

        {availabilityWeekday ? (
          <Stack
            aria-labelledby="availability-interval-details"
            flex={1}
            padding={2}
            textAlign="center"
          >
            <Stack
              alignItems="center"
              component="span"
              direction="row"
              marginBottom={4}
              justifyContent="space-between"
            >
              <Typography component="h4" variant="h6">
                Sempre aberto
              </Typography>

              <IOSSwitch
                checked={availabilityWeekday.is_always_open}
                onChange={(_, checked) => changeIsAlwaysOpenApi(checked)}
              />
            </Stack>

            <TransitionGroup>
              {!availabilityWeekday.is_always_open &&
                availabilityWeekday.intervals.map(interval => (
                  <Slide
                    in
                    direction="right"
                    key={interval.id}
                    mountOnEnter
                    unmountOnExit
                  >
                    <Stack
                      component="section"
                      data-testid="availability-interval"
                      marginBottom={4}
                    >
                      <Stack
                        component="span"
                        direction="row"
                        justifyContent="space-between"
                        marginBottom={1}
                      >
                        <Typography variant="body1">A:</Typography>
                        <TimePicker
                          value={getDateFromAvailabilityInterval(
                            interval.start
                          )}
                          onChange={newValue => {
                            if (!newValue) return;
                            editAvailabilityInterval({
                              ...interval,
                              start: formatDateToAvailabilityInterval(newValue),
                            });
                          }}
                          onAccept={() =>
                            changeAvailabilityIntervalApi({
                              start: interval.start,
                              id: interval.id,
                            })
                          }
                          testid="begin-hour"
                        />
                      </Stack>

                      <Stack
                        component="span"
                        direction="row"
                        justifyContent="space-between"
                        marginBottom={2}
                      >
                        <Typography variant="body1">Até:</Typography>
                        <TimePicker
                          value={getDateFromAvailabilityInterval(interval.end)}
                          onChange={newValue => {
                            if (!newValue) return;
                            editAvailabilityInterval({
                              ...interval,
                              end: formatDateToAvailabilityInterval(newValue),
                            });
                          }}
                          onAccept={() =>
                            changeAvailabilityIntervalApi({
                              end: interval.end,
                              id: interval.id,
                            })
                          }
                          testid="end-hour"
                        />
                      </Stack>

                      <Button
                        color="error"
                        onClick={() =>
                          removeAvailabilityIntervalApi(interval.id)
                        }
                        variant="outlined"
                      >
                        Remover
                      </Button>
                    </Stack>
                  </Slide>
                ))}
            </TransitionGroup>

            <Slide
              direction="up"
              in={
                availabilityWeekday.intervals.length === 0 &&
                !availabilityWeekday.is_always_open
              }
              mountOnEnter
              unmountOnExit
            >
              <Box
                sx={{
                  paddingTop: '20%',
                }}
              >
                <WorkOffIcon
                  sx={{
                    color: 'text.secondary',
                    fontSize: '40px',
                    marginBottom: 1,
                  }}
                />
                <Typography
                  color="text.secondary"
                  component="p"
                  textAlign="center"
                  variant="body1"
                >
                  Fechado neste dia da semana
                </Typography>
              </Box>
            </Slide>

            {(availabilityWeekday.intervals.length < 2 ||
              availabilityWeekday.is_always_open) && (
              <Button
                color="primary"
                disabled={availabilityWeekday.is_always_open}
                fullWidth
                onClick={addAvailabilityIntervalApi}
                sx={{
                  marginTop: 'auto',
                }}
                variant="contained"
              >
                Adicionar horários
              </Button>
            )}
          </Stack>
        ) : (
          <Box
            aria-labelledby="availability-hours"
            paddingX={2}
            sx={{
              flex: '1',
              overflowY: 'auto',
              paddingBottom: 3,
            }}
          >
            <Typography
              color="text.secondary"
              component="h3"
              id="availability-hours"
              marginBottom={2}
              variant="h6"
            >
              Horário
            </Typography>

            <Stack
              component="ul"
              direction="column"
              divider={<Divider />}
              margin={0}
              padding={0}
              spacing={1}
            >
              {state.availabilities.map(
                ({ weekday, intervals, is_always_open }) => (
                  <Stack
                    aria-labelledby={`${WEEKDAY_TEST_ID[weekday]}-availability-interval`}
                    component="li"
                    data-testid="availability-weekday"
                    direction="row"
                    justifyContent="space-between"
                    key={weekday}
                  >
                    <Box
                      component="aside"
                      data-testid={`${WEEKDAY_TEST_ID[weekday]}-availability-interval`}
                    >
                      <Typography
                        component="h4"
                        data-testid="weekday"
                        id={`${WEEKDAY_TEST_ID[weekday]}-availability-interval`}
                        variant="body1"
                      >
                        {WEEKDAYS[weekday]}
                      </Typography>

                      {getFirstValueTrue([
                        is_always_open && (
                          <Typography
                            color="text.secondary"
                            component="p"
                            variant="body1"
                            data-testid="is_always_open"
                          >
                            Aberto 24 horas
                          </Typography>
                        ),
                        intervals.length === 0 && (
                          <Typography
                            color="text.secondary"
                            component="p"
                            variant="body1"
                            data-testid="is_closed"
                          >
                            Fechado
                          </Typography>
                        ),
                        intervals.map(({ id, start, end }) => (
                          <Typography
                            color="text.secondary"
                            component="p"
                            variant="body1"
                            key={id}
                          >
                            {formatHour(start)} - {formatHour(end)}
                          </Typography>
                        )),
                      ])}
                    </Box>

                    <IconButton
                      onClick={() => editWeekday(weekday)}
                      sx={{
                        color: 'text.primary',
                        height: '40px',
                        marginTop: '4px',
                      }}
                    >
                      <ArrowForwardIosIcon />
                    </IconButton>
                  </Stack>
                )
              )}
            </Stack>
          </Box>
        )}
      </Box>
    </Modal>
  );
};
export type { IAvailabilitiesProps };
